import '@/playlist/games/laneChooser/styles/lane-chooser-end-screen.scss';

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import classNames from 'classnames';
import Lottie from 'lottie-react';

import * as playlistActions from '@/playlist/Actions';
import useAudioPlayer from '@/services/hocs/useAudioPlayer';
import useResponsive from '@/services/hocs/useResponsive';
import { useLaneChooserTheme } from '@/playlist/games/laneChooser/theme';
import { EndOutcome } from '@/playlist/types';

import Button from '@/components/button/Button';

import resultFailedSfx from '@/playlist/games/laneChooser/assets/sounds/result-failed.mp3';
import resultVictorySfx from '@/playlist/games/laneChooser/assets/sounds/result-victory.mp3';
import surfingSpeedwayIntro from '@/playlist/games/laneChooser/assets/sounds/surfing-speedway-intro.mp3';

import type { PlayList } from '@/playlist/types';

export type EndScreenProps = {
	className?: string;
	outcome: EndOutcome;
	playList?: PlayList | null;
	hasAnswered?: boolean;
	onClose: () => void;
	finishPlaylistRequest: (
		playlistId: number | string,
		freePlayCategoryId?: number | string,
		outcome?: EndOutcome,
	) => void;
};

const EndScreen = ({
	className,
	outcome,
	playList,
	hasAnswered = true,
	onClose,
	finishPlaylistRequest,
}: EndScreenProps) => {
	const { t } = useTranslation();
	const { isMobile, isTablet, isPortrait, isLandscape } = useResponsive();
	const { id: playlistIdFromRoute, categoryId: freePlayCategoryIdFromRoute } = useParams();
	const playlistId = (playlistIdFromRoute ?? playList?.id) as number | string | undefined;
	const theme = useLaneChooserTheme();

	const isSuccess = outcome === EndOutcome.SUCCEEDED;
	const isFailed = outcome === EndOutcome.LOST;

	useEffect(() => {
		// Only send finish request for LOST/ABANDONED outcomes
		// SUCCEEDED is handled by the callback after the last answer request
		if (playlistId != null && hasAnswered && outcome !== EndOutcome.SUCCEEDED) {
			finishPlaylistRequest(playlistId, freePlayCategoryIdFromRoute, outcome);
		}
	}, [playlistId, freePlayCategoryIdFromRoute, outcome, hasAnswered, finishPlaylistRequest]);

	const { play: playEndMusic, stop: stopEndMusic } = useAudioPlayer({
		src: surfingSpeedwayIntro,
		loop: true,
		volume: 0.5,
	});
	const { play: playVictorySfx, stop: stopVictorySfx } = useAudioPlayer({
		src: resultVictorySfx,
		volume: 0.7,
	});
	const { play: playFailedSfx, stop: stopFailedSfx } = useAudioPlayer({
		src: resultFailedSfx,
		volume: 0.7,
	});

	const animationsFailed = theme.animations.endScreen.failed;
	const animationsSuccess = theme.animations.endScreen.success;

	const animationData = useMemo(() => {
		if (isSuccess) {
			return isMobile
				? isPortrait
					? animationsSuccess.mobile
					: animationsSuccess.tabletLandscape
				: isTablet
				? isPortrait
					? animationsSuccess.tabletPortrait
					: animationsSuccess.tabletLandscape
				: isLandscape
				? animationsSuccess.desktop
				: animationsSuccess.tabletPortrait;
		} else if (isFailed) {
			return isMobile
				? isPortrait
					? animationsFailed.mobile
					: animationsFailed.tabletLandscape
				: isTablet
				? isPortrait
					? animationsFailed.tabletPortrait
					: animationsFailed.tabletLandscape
				: isLandscape
				? animationsFailed.desktop
				: animationsFailed.tabletPortrait;
		}
		return null;
	}, [
		isMobile,
		isTablet,
		isPortrait,
		isLandscape,
		isSuccess,
		isFailed,
		animationsSuccess,
		animationsFailed,
	]);

	const preserveAspectRatio = isLandscape ? 'xMidYMid slice' : 'xMaxYMid slice';

	useEffect(() => {
		playEndMusic();
		return () => {
			stopEndMusic();
		};
	}, [playEndMusic, stopEndMusic]);

	useEffect(() => {
		if (isSuccess) {
			playVictorySfx();
		} else {
			playFailedSfx();
		}
		return () => {
			stopVictorySfx();
			stopFailedSfx();
		};
	}, [isSuccess, playFailedSfx, playVictorySfx, stopFailedSfx, stopVictorySfx]);

	return (
		<div
			className={classNames(
				'lane-chooser-end-screen',
				`lane-chooser-end-screen--${theme.key}`,
				{
					'lane-chooser-end-screen--succeeded': isSuccess,
					'lane-chooser-end-screen--failed': isFailed,
				},
				className,
			)}
		>
			<div className="lane-chooser-end-screen__bg" aria-hidden="true">
				<Lottie
					animationData={animationData}
					loop
					autoplay
					rendererSettings={{ preserveAspectRatio }}
				/>
			</div>
			<div className="lane-chooser-end-screen__inner">
				<h2 className="lane-chooser-end-screen__title">
					{isSuccess
						? t('lane_chooser.end_screen.title.succeeded')
						: t('lane_chooser.end_screen.title.failed')}
				</h2>
				<p className="lane-chooser-end-screen__description">
					{isSuccess
						? t('lane_chooser.end_screen.description.succeeded')
						: t('lane_chooser.end_screen.description.failed')}
				</p>
				<div className="lane-chooser-end-screen__actions">
					<Button
						type="primary"
						iconName="icon-arrow-circle-right"
						title={t('lane_chooser.end_screen.button.next')}
						onClick={onClose}
					/>
				</div>
			</div>
		</div>
	);
};

function mapStateToProps({ playlistReducer }: any) {
	return {
		playList: playlistReducer.playList,
	};
}

const mapDispatchToProps = {
	finishPlaylistRequest: playlistActions.finishPlaylistRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(EndScreen);
